/**
 * EventPady+ — Platform Admin API
 *
 * All functions call SUPER_ADMIN-only endpoints.
 * These must never be called for non-SUPER_ADMIN users.
 */
import { apiClient } from '@/services/api/client'
import { API_ENDPOINTS } from '@/services/api/endpoints'
import type {
  PaginatedResponse,
  PlatformOrgDetail,
  PlatformOrgListParams,
  PlatformOrgSummary,
  PlatformOverview,
} from '@/types'

export const platformAdminApi = {
  /** GET /platform-admin/overview/ */
  getOverview: async (): Promise<PlatformOverview> => {
    const res = await apiClient.get<{ data: Record<string, unknown> }>(
      API_ENDPOINTS.PLATFORM_ADMIN.OVERVIEW,
    )
    const raw = res.data.data as Record<string, unknown>
    // Backend returns plans_breakdown as a plain dict {planName: count}.
    // Frontend type expects Array<{plan_name, count}>. Transform here.
    const rawBreakdown = (raw.plans_breakdown ?? {}) as Record<string, number>
    const plans_breakdown = Object.entries(rawBreakdown).map(
      ([plan_name, count]) => ({ plan_name, count }),
    )
    return {
      total_organizations:   raw.total_organizations   as number,
      active_organizations:  raw.active_organizations  as number,
      flagged_organizations: raw.flagged_organizations as number,
      total_visitors:        raw.total_visitors        as number,
      total_sms_sent:        raw.total_sms_sent        as number,
      // Backend key is open_compliance_events; frontend type is open_compliance
      open_compliance: (raw.open_compliance_events ?? raw.open_compliance ?? 0) as number,
      plans_breakdown,
    }
  },

  /** GET /platform-admin/organizations/ */
  listOrganizations: async (
    params: PlatformOrgListParams = {},
  ): Promise<PaginatedResponse<PlatformOrgSummary>> => {
    const res = await apiClient.get<PaginatedResponse<PlatformOrgSummary>>(
      API_ENDPOINTS.PLATFORM_ADMIN.ORGANIZATIONS,
      { params },
    )
    return res.data
  },

  /** GET /platform-admin/organizations/:id/ */
  getOrganization: async (id: string): Promise<PlatformOrgDetail> => {
    const res = await apiClient.get<{ data: PlatformOrgDetail }>(
      API_ENDPOINTS.PLATFORM_ADMIN.ORG_DETAIL(id),
    )
    return res.data.data
  },
}
